import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { TONE, HEALTH_DOT, type Tone } from "@recursive-staff/ui";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

export function Clients() {
  const { data, isLoading, error } = useQuery({ queryKey: ["clients"], queryFn: api.clients });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Clients</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>{data.length} MLS tenants of the Recursive platform</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 14 }}>
        {data.map((c) => (
          <Link key={c.id} to="/clients/$clientId" params={{ clientId: c.id }} style={{ textDecoration: "none", color: "inherit" }}>
            <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: HEALTH_DOT[c.health] }} />
                <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{c.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: "#f0efe9", color: "#6b706a" }}>{c.plan}</span>
              </div>
              <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 3 }}>{c.host} · {c.env}</div>
              <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
                <Stat label="Users" value={c.users.toLocaleString()} />
                <Stat label="Open" value={String(c.open)} />
                <Stat label="At risk" value={String(c.atRisk)} tone={c.atRisk ? "bad" : undefined} />
                <Stat label="CSAT" value={c.csat} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Page>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, fontFamily: "'Space Mono',monospace", marginTop: 3, color: tone ? TONE[tone].fg : "#1d241f" }}>{value}</div>
    </div>
  );
}

export function Client360() {
  const { clientId } = useParams({ from: "/clients/$clientId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["client", clientId], queryFn: () => api.client(clientId) });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Link to="/clients" style={{ fontSize: 12, fontWeight: 600, color: "#8a8e88" }}>‹ Clients</Link>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: HEALTH_DOT[data.health] }} />
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>{data.name}</h1>
          </div>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>{data.host} · {data.env} · {data.plan} · {data.timezone}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
        <KpiCell label="Members" value={data.users.toLocaleString()} icon="users" />
        <KpiCell label="Open tickets" value={String(data.openTickets)} icon="inbox" />
        <KpiCell label="At risk" value={String(data.stats?.atRisk ?? 0)} icon="timer" />
        <KpiCell label="CSAT" value={data.stats?.csat ?? "—"} icon="gauge" />
        <KpiCell label="Data residency" value={data.dataResidency.toUpperCase()} icon="shield" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(360px,1fr))", gap: 16 }}>
        <Panel title="Projects" icon="folder-kanban">
          {data.projects.length === 0 && <Empty>No active projects.</Empty>}
          {data.projects.map((p) => (
            <div key={p.id} style={{ padding: "11px 0", borderTop: "1px solid #f4f3ee" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{p.name}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: TONE[(p.scheduleHealth as Tone) ?? "neutral"].bg, color: TONE[(p.scheduleHealth as Tone) ?? "neutral"].fg }}>{p.status}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <div style={{ flex: 1, height: 6, background: "#f0efe9", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: `${p.progressPct}%`, height: "100%", background: "#006747" }} />
                </div>
                <span style={{ fontSize: 11, fontFamily: "'Space Mono',monospace", color: "#4a504a" }}>{p.progressPct}%</span>
                <span style={{ fontSize: 11, color: "#8a8e88" }}>· go-live {p.goLiveDate}</span>
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="Incidents" icon="siren">
          {data.incidents.length === 0 && <Empty>No incidents.</Empty>}
          {data.incidents.map((i) => (
            <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 0", borderTop: "1px solid #f4f3ee" }}>
              <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#8a8e88" }}>{i.id}</span>
              <span style={{ fontSize: 13, flex: 1 }}>{i.title}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: i.severity === "Major" ? "#fbe9e7" : "#fbf2dc", color: i.severity === "Major" ? "#b3261e" : "#9a6a12" }}>{i.status}</span>
            </div>
          ))}
        </Panel>

        <ClientMembers clientId={clientId} total={data.users} preview={data.people} />
      </div>
    </Page>
  );
}

const initialsOf = (name: string) =>
  (name || "?").split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();

type MemberRow = { id: string; name: string; email: string | null; role: string | null };

/** Searchable member panel — the roster can be 100k+, so it's search-driven and
 * bounded. Empty search shows the preview from the Client-360 payload. */
function ClientMembers({
  clientId,
  total,
  preview,
}: {
  clientId: string;
  total: number;
  preview: { id: string; name: string; initials: string; email: string | null; role: string | null }[];
}) {
  const [q, setQ] = useState("");
  const searching = q.trim().length >= 2;
  const results = useQuery({
    queryKey: ["client-members", clientId, q.trim()],
    queryFn: () => api.clientMembers(clientId, q),
    enabled: searching,
    placeholderData: keepPreviousData,
  });
  const rows: MemberRow[] = searching ? (results.data?.members ?? []) : preview;

  return (
    <Panel title={`Members (${total.toLocaleString()})`} icon="users">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search members by name or email…"
        style={{ width: "100%", boxSizing: "border-box", margin: "2px 0 6px", padding: "8px 10px", fontSize: 12.5, border: "1px solid #e7e5df", borderRadius: 6, outline: "none" }}
      />
      {searching && results.isFetching && <Empty>Searching…</Empty>}
      {!results.isFetching && rows.length === 0 && (
        <Empty>{searching ? "No matches." : "No members."}</Empty>
      )}
      {rows.map((p) => (
        <Link key={p.id} to="/person/$personId" params={{ personId: p.id }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #f4f3ee", textDecoration: "none", color: "inherit" }}>
          <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11, flex: "none" }}>{initialsOf(p.name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
            <div style={{ fontSize: 11.5, color: "#8a8e88", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[p.role, p.email].filter(Boolean).join(" · ")}</div>
          </div>
        </Link>
      ))}
      {!searching && total > preview.length && (
        <div style={{ padding: "10px 0 4px", fontSize: 11.5, color: "#8a8e88" }}>
          Showing {preview.length} of {total.toLocaleString()} — search to find a specific member.
        </div>
      )}
    </Panel>
  );
}

function KpiCell({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "#8a8e88" }}>{label}</span>
        <Icon name={icon} size={15} color="#8a8e88" />
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, fontFamily: "'Space Mono',monospace", marginTop: 10 }}>{value}</div>
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Icon name={icon} size={15} color="#006747" />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: "12px 0", fontSize: 12.5, color: "#8a8e88" }}>{children}</div>;
}
